/**
 * Template Queue Manager
 * Handles sequential processing of multiple draw templates
 */

export class TemplateQueueManager {
  constructor() {
    this.queue = []
    this.currentIndex = -1
    this.projectId = null
    this.drawNumber = null
    this.appendMode = false
    this.onQueueComplete = null
  }

  /**
   * Initialize queue with templates
   * @param {Object} options - { projectId, drawNumber, appendMode, templates, onComplete }
   */
  initialize({ projectId, drawNumber, appendMode, templates, onComplete }) {
    console.log('🔧 INITIALIZE CALLED WITH:', { projectId, drawNumber, appendMode, templates: templates?.length })
    
    this.projectId = projectId
    this.drawNumber = drawNumber
    this.appendMode = appendMode || false
    this.queue = templates.map((template, index) => ({
      ...template,
      index,
      status: template.status || 'pending', // preserve existing status or default to pending
      templateId: template.templateId || null, // preserve existing templateId
      fileName: template.fileName || null, // preserve existing fileName
      error: template.error || null
    }))
    this.currentIndex = -1
    this.onQueueComplete = onComplete || null

    console.log('🔧 AFTER SETTING:', { 
      thisProjectId: this.projectId, 
      thisDrawNumber: this.drawNumber,
      thisAppendMode: this.appendMode
    })

    // Save to localStorage for persistence
    this.saveToLocalStorage()

    console.log('🎯 Queue initialized:', {
      projectId,
      drawNumber,
      templates: this.queue.length
    })
  }

  /**
   * Get current queue state
   */
  getState() {
    return {
      queue: this.queue,
      currentIndex: this.currentIndex,
      current: this.getCurrentTemplate(),
      hasNext: this.hasNext(),
      hasPrevious: this.currentIndex > 0,
      isComplete: this.isQueueComplete(),
      progress: this.getProgress()
    }
  }

  /**
   * Get current template being processed
   */
  getCurrentTemplate() {
    if (this.currentIndex >= 0 && this.currentIndex < this.queue.length) {
      return this.queue[this.currentIndex]
    }
    return null
  }

  /**
   * Get next template in queue
   */
  getNextTemplate() {
    for (let i = 0; i < this.queue.length; i++) {
      if (this.queue[i].status === 'pending') {
        return this.queue[i]
      }
    }
    return null
  }

  /**
   * Check if there's a next template to process
   */
  hasNext() {
    return this.queue.some(t => t.status === 'pending')
  }

  /**
   * Move to next template
   */
  next() {
    const nextTemplate = this.getNextTemplate()
    if (nextTemplate) {
      this.currentIndex = nextTemplate.index
      nextTemplate.status = 'processing'
      this.saveToLocalStorage()
      console.log('➡️ Moving to next template:', nextTemplate.displayName)
      return nextTemplate
    }
    return null
  }

  /**
   * Mark current template as completed
   */
  markCompleted(templateId) {
    if (this.currentIndex >= 0 && this.currentIndex < this.queue.length) {
      this.queue[this.currentIndex].status = 'completed'
      this.queue[this.currentIndex].templateId = templateId
      this.saveToLocalStorage()
      console.log('✅ Template completed:', this.queue[this.currentIndex].displayName)
    }

    // Check if queue is complete
    if (this.isQueueComplete()) {
      console.log('🎉 All templates processed!')
      if (this.onQueueComplete) {
        this.onQueueComplete()
      }
    }
  }

  /**
   * Mark current template as failed
   */
  markFailed(error) {
    if (this.currentIndex >= 0 && this.currentIndex < this.queue.length) {
      this.queue[this.currentIndex].status = 'failed'
      this.queue[this.currentIndex].error = error
      this.saveToLocalStorage()
      console.error('❌ Template failed:', this.queue[this.currentIndex].displayName, error)
    }
  }

  /**
   * Retry failed template
   */
  retry(index) {
    if (index >= 0 && index < this.queue.length && this.queue[index].status === 'failed') {
      this.queue[index].status = 'pending'
      this.queue[index].error = null
      this.saveToLocalStorage()
      console.log('🔄 Retrying template:', this.queue[index].displayName)
    }
  }

  /**
   * Check if all templates are processed
   */
  isQueueComplete() {
    return this.queue.length > 0 && this.queue.every(t => t.status === 'completed')
  }

  /**
   * Get progress percentage
   */
  getProgress() {
    if (this.queue.length === 0) return 0
    const completed = this.queue.filter(t => t.status === 'completed').length
    return Math.round((completed / this.queue.length) * 100)
  }

  /**
   * Get summary stats
   */
  getSummary() {
    return {
      total: this.queue.length,
      completed: this.queue.filter(t => t.status === 'completed').length,
      pending: this.queue.filter(t => t.status === 'pending').length,
      processing: this.queue.filter(t => t.status === 'processing').length,
      failed: this.queue.filter(t => t.status === 'failed').length
    }
  }

  /**
   * Save queue state to localStorage
   */
  saveToLocalStorage() {
    const state = {
      projectId: this.projectId,
      drawNumber: this.drawNumber,
      appendMode: this.appendMode,
      queue: this.queue,
      currentIndex: this.currentIndex,
      timestamp: Date.now()
    }
    console.log('💾 SAVING TO LOCALSTORAGE:', { 
      projectId: this.projectId, 
      stateProjectId: state.projectId,
      drawNumber: this.drawNumber, 
      queueLength: this.queue.length,
      fullState: state
    });
    localStorage.setItem('dda_template_queue', JSON.stringify(state))
    console.log('💾 SAVED! Verify:', localStorage.getItem('dda_template_queue')?.substring(0, 200))
  }

  /**
   * Load queue state from localStorage
   */
  loadFromLocalStorage() {
    try {
      const saved = localStorage.getItem('dda_template_queue')
      if (saved) {
        const state = JSON.parse(saved)
        
        // Check if saved state is recent (within 24 hours)
        const ageHours = (Date.now() - state.timestamp) / (1000 * 60 * 60)
        if (ageHours < 24) {
          this.projectId = state.projectId
          this.drawNumber = state.drawNumber
          this.appendMode = state.appendMode || false
          this.queue = state.queue
          this.currentIndex = state.currentIndex
          console.log('📦 Loaded queue from localStorage:', this.getSummary())
          return true
        } else {
          console.log('⏰ Saved queue is too old, starting fresh')
          this.clearLocalStorage()
        }
      }
    } catch (error) {
      console.error('Error loading queue from localStorage:', error)
    }
    return false
  }

  /**
   * Clear queue from localStorage
   */
  clearLocalStorage() {
    localStorage.removeItem('dda_template_queue')
    console.log('🗑️ Cleared queue from localStorage')
  }

  /**
   * Reset queue completely
   */
  reset() {
    this.queue = []
    this.currentIndex = -1
    this.projectId = null
    this.drawNumber = null
    this.appendMode = false
    this.onQueueComplete = null
    this.clearLocalStorage()
    console.log('🔄 Queue reset')
  }
}

// Export singleton instance
export default new TemplateQueueManager()

